/**
 * components/shared/NedryModal.jsx
 * ==================================
 * Policy violation modal — the "uh uh uh" moment.
 *
 * The image is optional and per-instructor. The backend includes `image_url`
 * and `image_alt` in the violation payload when the course's instructor has
 * uploaded one; when they haven't, this renders as a plain text modal.
 *
 * There is deliberately no built-in default image. See NEDRY-UPLOADS.md.
 */

import { useEffect, useRef } from 'react';
import styles from './NedryModal.module.css';

export default function NedryModal({ message, code, imageUrl, imageAlt, onClose }) {
  const defaultMsg = "Nah uh uh — you didn't follow the syllabus!";
  const closeRef   = useRef(null);
  const headingId  = 'nedry-message';

  // Escape to dismiss, and focus starts on the only action. Without this the
  // modal traps keyboard users behind a dialog they can see but can't act on.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    closeRef.current?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div
        className={styles.modal}
        onClick={e => e.stopPropagation()}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={headingId}
      >

        {imageUrl && (
          <>
            <div className={styles.gifWrap}>
              <img
                src={imageUrl}
                /* Alt text is supplied by the instructor at upload time. Falling
                   back to empty rather than inventing a description: an image we
                   can't describe is decorative as far as a screen reader goes,
                   and the message below carries the actual meaning. */
                alt={imageAlt || ''}
                className={styles.gif}
              />
            </div>
            {/* Requested attribution. Outside .gifWrap deliberately — that
                element clips overflow and caps its height, so anything inside
                it alongside the image gets cut. */}
            <p className={styles.attribution}>
              Image supplied by your instructor, not by Gradiance.
            </p>
          </>
        )}

        <div className={styles.body}>
          <p className={styles.message} id={headingId}>{message || defaultMsg}</p>
          {code && (
            <p className={styles.code}>Policy: {code.replace(/_/g, ' ')}</p>
          )}
          <button
            ref={closeRef}
            className={`btn-primary ${styles.closeBtn}`}
            onClick={onClose}
          >
            Got it
          </button>
        </div>

      </div>
    </div>
  );
}
